import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { BAN_FROM_ORG_MUTATION } from "@/api/mutations/banFromOrg";
import {
  BanFromOrgMutation,
  BanFromOrgMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";

export interface UseBanFromOrgParams {
  onCompleted?: (data: BanFromOrgMutation) => void;
  onError?: (error: unknown) => void;
}

export interface BanFromOrgResult {
  success: boolean;
  errors: {
    message: string;
    title: string;
  }[];
}

export interface UseBanFromOrgReturn {
  loading: boolean;
  error: unknown;
}

export const useBanFromOrg = ({
  onCompleted,
  onError,
}: UseBanFromOrgParams = {}): [
  (orgId: string, userId: string) => Promise<BanFromOrgResult>,
  UseBanFromOrgReturn,
] => {
  const [banFromOrgMutation, { loading, error }] = useMutation<
    BanFromOrgMutation,
    BanFromOrgMutationVariables
  >(BAN_FROM_ORG_MUTATION, {
    onCompleted,
    onError,
    update: (cache, { data }, { variables }) => {
      if (
        !data?.createBannedFromOrg ||
        data.createBannedFromOrg.errors?.length ||
        !variables?.userId
      ) {
        return;
      }

      const userId = variables.userId;
      cache.evict({ id: `Student:${userId}` });
      cache.modify({
        fields: {
          studentList(existingList, { readField }) {
            if (!existingList?.edges) return existingList;

            return {
              ...existingList,
              edges: existingList.edges.filter(
                (edge: { node: { id: string } }) =>
                  readField("id", edge.node) !== userId,
              ),
              totalCount: Math.max(0, (existingList.totalCount || 0) - 1),
            };
          },
        },
      });
      cache.gc();
    },
  });

  const banFromOrg = useCallback(
    async (orgId: string, userId: string): Promise<BanFromOrgResult> => {
      try {
        const result = await banFromOrgMutation({
          variables: { orgId, userId },
        });

        const errorHandler = MutationErrorHandler.fromResult(
          result,
          result.errors,
        );
        return errorHandler.toResult();
      } catch (err) {
        const errorHandler = MutationErrorHandler.fromException(err);
        return errorHandler.toResult();
      }
    },
    [banFromOrgMutation],
  );

  return [banFromOrg, { loading, error }];
};
